import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ViewAvailabilityController } from '@/engine/app/modules/ui/UIConfigSchema';
import type { UIConfigResolver } from '@/engine/app/modules/ui';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { CommandProcessor } from '@/engine/app/modules/core/services/CommandProcessor';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity';
import type { AppController } from '@/engine/app/App.controller';

@injectable()
export class ApproveController {
  private readonly _appController: AppController;

  private readonly _viewsService: ViewsService;

  private readonly _viewAvailabilityController: ViewAvailabilityController | undefined;

  private readonly _commandProcessor: CommandProcessor;

  public constructor(
    @inject(TYPES.AppController) appController: AppController,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.UIConfigResolver) uiConfigResolver: UIConfigResolver,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: CommandProcessor
  ) {
    this._appController = appController;
    this._viewsService = viewsService;
    this._viewAvailabilityController = uiConfigResolver.getOptionalValueByDescriptor('viewAvailabilityController');
    this._commandProcessor = commandProcessor;
  }

  private _getNextNotApprovedStep(): ViewEntity | undefined {
    return this._viewsService.views.find((view) => !view.getApprovedStatus());
  }

  public hasNotApprovedSteps(): boolean {
    return this._getNextNotApprovedStep() !== undefined;
  }

  public getApproveStepButtonTooltip(): string {
    return this.getApprovePlanButtonTooltip();
  }

  public canApproveStep(): boolean {
    const caseState = this._appController.getCaseState();
    if (!caseState.userPermissions.canApproveCase || this._appController.isCineloopActive) {
      return false;
    }
    if (this._viewAvailabilityController) {
      const nextNotApprovedView = this._getNextNotApprovedStep();
      const isNextViewEnabled = nextNotApprovedView
        ? this._viewAvailabilityController.isViewEnabled(nextNotApprovedView?.getDescriptor())
        : false;
      if (nextNotApprovedView && !isNextViewEnabled) {
        return false;
      }
    }
    return this._viewsService.currentView?.getApprovedStatus() === false;
  }

  public approveStep(): void {
    if (!this.canApproveStep()) {
      return;
    }
    const currentView = this._viewsService.currentView;
    if (currentView?.getApprovedStatus() !== false) {
      return;
    }
    let previousView: ViewEntity | undefined = undefined;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.executeAnonymousCommand(
      (): Promise<undefined> => {
        this._viewsService.currentView?.setApprovedStatus(true);
        const nextNotApprovedView = this._getNextNotApprovedStep();
        if (nextNotApprovedView) {
          previousView = currentView;
          this._viewsService.setCurrentView(nextNotApprovedView);
        }
        return Promise.resolve(undefined);
      },
      (): Promise<undefined> => {
        if (previousView) {
          this._viewsService.setCurrentView(previousView);
        }
        this._viewsService.currentView?.setApprovedStatus(false);
        return Promise.resolve(undefined);
      }
    );
  }

  public getRejectPlanButtonTooltip(): string {
    const caseState = this._appController.getCaseState();
    return !caseState.userPermissions.canRejectCase
      ? 'You have no permissions to reject this plan'
      : this._appController.isCineloopActive
        ? 'Rejection is not allowed during cineloop'
        : '';
  }

  public canRejectPlan(): boolean {
    const caseState = this._appController.getCaseState();
    return caseState.userPermissions.canRejectCase && !this._appController.isCineloopActive;
  }

  public async rejectPlan(): Promise<void> {
    if (!this.canRejectPlan()) {
      return;
    }
    if (
      confirm(
        'You clicked the Request Review button. To request the 3D model and landmarks be reviewed and/or revised click OK; then specify the reason on the next page. Please note, any potential changes made to the plan will be lost. To go back to the plan, click Cancel.'
      )
    ) {
      await this._appController.rejectCase();
    }
  }

  public getApprovePlanButtonTooltip(): string {
    const caseState = this._appController.getCaseState();
    return !caseState.userPermissions.canApproveCase
      ? 'You have no permissions to approve this plan'
      : this._appController.isCineloopActive
        ? 'Approval is not allowed during cineloop'
        : '';
  }

  public canApprovePlan(): boolean {
    const caseState = this._appController.getCaseState();
    return caseState.userPermissions.canApproveCase && !this._appController.isCineloopActive;
  }

  public async approvePlan(): Promise<void> {
    if (!this.canApprovePlan()) {
      return;
    }
    if (
      confirm(
        'By continuing you confirm that you have validated the presented workflow models, landmarks and measurements on the medical images and want to approve your case using the current device position and size.'
      )
    ) {
      await this._appController.approveCase();
    }
  }
}
